/** O card do Kanban (DESIGN.md § 3.1).
 *
 * Quatro decisões que o desenho impõe e o código precisa honrar:
 *
 * * **Contador zerado não aparece.** Um "0 documentos" ocupa a mesma linha que
 *   um "3 documentos" e não diz nada — o board é lido por varredura.
 * * **Dinheiro e prazo em fonte de dados.** Ela alinha dígitos em coluna, e a
 *   coluna é lida verticalmente comparando valores.
 * * **Âmbar e carmim nunca por estética.** Se aparecem, algo exige ação — e é
 *   por isso que a etiqueta acesa aqui é a do card **frio**, não a do quente.
 *   Um board saudável fica verde e papel; se ele se enche de âmbar, é porque
 *   há mesmo o que fazer.
 * * **A régua de silêncio fica.** O desenho novo não a trazia, e ela é o
 *   elemento-assinatura aprovado: a etiqueta diz a faixa, a régua diz a forma
 *   do silêncio — dois dias parados depois de uma semana de conversa não é a
 *   mesma coisa que dois dias de nada.
 *
 * **Arrasta e tem menu, nesta ordem de importância invertida.** O card é
 * arrastável (ADR-0016), e o menu `⋯` continua sendo o caminho que não depende
 * do gesto — para teclado, para leitor de tela e para quem prefere escolher a
 * etapa numa lista. O menu é um `<details>`: abre por teclado sem uma linha de
 * JavaScript.
 *
 * O arrasto exige 6px de deslocamento para começar (`activationConstraint` no
 * quadro). Sem isso, os `listeners` do dnd-kit no `<article>` engoliriam o
 * clique no título e no menu, e o card viraria só arrastável.
 */

import { useEffect, useRef } from "react";

import { useDraggable } from "@dnd-kit/core";

import { ReguaDeSilencio } from "@/shared/ui/ReguaDeSilencio";
import { iniciais, primeiroNome } from "@/shared/ui/iniciais";

import {
  SEM_VALOR,
  descreverContato,
  formatarData,
  formatarDinheiro,
  horaDoContato,
  resumirSilencio,
  sinalDosUltimosDias,
  temValor,
} from "./formato";

import type { Card } from "./api";

interface Props {
  card: Card;
  etapas: readonly { id: string; nome: string }[];
  etapaAtual: string;
  aoMover: (paraEtapa: string) => void;
  /** Abre a ficha. Com `true`, ela abre já com o formulário de compromisso —
   * o atalho do menu, para quem acabou de falar com o cliente. */
  aoAbrir: (marcarCompromisso?: boolean) => void;
  movendo?: boolean;
}

export function CardDaOportunidade({
  card,
  etapas,
  etapaAtual,
  aoMover,
  aoAbrir,
  movendo = false,
}: Props) {
  const destinos = etapas.filter((etapa) => etapa.id !== etapaAtual);
  const etiqueta = etiquetaDo(card);
  // Nome vazio é o mesmo que ausente para a tela, e tratá-los juntos evita um
  // avatar com uma letra só de espaço em branco.
  const responsavel =
    card.owner_name !== null && card.owner_name.trim() !== "" ? card.owner_name : null;

  // Oportunidade fechada não se move: `Opportunity.mover_para` chama
  // `_exigir_aberta` e recusa. Deixar o card arrastável para ele voltar sozinho
  // depois do `422` seria prometer um gesto que o domínio nega — o caminho para
  // um negócio fechado é reabrir.
  const arrastavel = card.status === "open";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: arrastando,
  } = useDraggable({ id: card.id, disabled: !arrastavel });

  return (
    <article
      ref={setNodeRef}
      // Os atributos do dnd-kit só entram no card que **de fato** arrasta.
      //
      // Com `disabled`, a biblioteca ainda devolve `aria-disabled="true"` e o
      // `aria-roledescription` de item arrastável. O primeiro é o que dói: ele
      // fica no `<article>`, e um `aria-disabled` no ancestral desabilita, para
      // tecnologia assistiva e para automação, **tudo o que está dentro** —
      // inclusive o título, que é botão e continua abrindo a ficha. O card de
      // um negócio fechado se anunciava inteiro como desabilitado enquanto
      // respondia normalmente ao clique. O segundo mente do mesmo jeito:
      // anunciar "arrastável" o que o domínio recusa mover.
      {...(arrastavel ? attributes : {})}
      {...(arrastavel ? listeners : {})}
      // Depois do spread, e de propósito: o dnd-kit põe `role="button"` no nó
      // arrastável, e isso apagava o papel de `article` do card. O que o leitor
      // de tela precisa saber aqui é que existe um artigo com título, valor e
      // responsável — o `aria-roledescription` que a biblioteca também injeta
      // continua anunciando que ele é arrastável, e o `tabIndex` dela mantém o
      // card alcançável para o arrasto por teclado.
      role="article"
      aria-busy={movendo}
      style={
        transform === null
          ? undefined
          : // `rotate(1.5deg)` é do DESIGN.md § 3.1: o card sai do alinhamento
            // da coluna enquanto está na mão, e é isso que faz o gesto parecer
            // pegar em vez de empurrar.
            {
              transform: `translate3d(${String(transform.x)}px, ${String(transform.y)}px, 0) rotate(1.5deg)`,
            }
      }
      className={[
        "overflow-hidden rounded-lg border bg-superficie shadow-e1",
        "transition-[box-shadow,border-color,opacity] duration-150",
        "hover:border-texto-tenue/40 hover:shadow-e2",
        // Contato marcado para hoje (SPEC-AGD-002): a borda verde é o que
        // responde "por onde começo" no instante em que a pessoa entra. Cor
        // sozinha não basta — a tarja escrita logo abaixo carrega o horário, e
        // é ela que fala com quem não distingue as duas cores.
        card.has_contact_today ? "border-acao" : "border-borda",
        card.status === "open" ? "cursor-grab active:cursor-grabbing" : "",
        // `is_stale` esmaece o card: ele continua legível, mas cede atenção
        // para o que está em movimento.
        card.is_stale ? "opacity-[0.82]" : "",
        movendo ? "opacity-60" : "",
        arrastando ? "z-20 shadow-e3" : "",
      ].join(" ")}
    >
      {/* A régua mede **contato**, e não movimento do card. O que a alimenta é
          `signal_days`, derivado da timeline pelo projetor: um bit por dia em
          que alguém de fato falou com o cliente. */}
      <ReguaDeSilencio
        diasComSinal={sinalDosUltimosDias(card.signal_days)}
        descricao={descreverContato(card.signal_days)}
      />

      <div className="flex flex-col gap-3 p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col gap-0.5">
            {/* O título é o gatilho, e é um `<button>`: o card inteiro
                clicável engoliria o menu, e uma `<div onClick>` não chega por
                teclado nem se anuncia como ação. */}
            <h3 className="font-display text-body text-texto">
              <button
                type="button"
                // Envolvido, e não `onClick={aoAbrir}`: o React passaria o
                // `MouseEvent` como primeiro argumento, e um evento é truthy —
                // clicar no título abriria o formulário de compromisso.
                onClick={() => {
                  aoAbrir();
                }}
                className="line-clamp-2 text-left leading-snug hover:text-acao focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao-viva"
              >
                {card.title}
              </button>
            </h3>
            {card.company_name !== null && card.company_name !== "" && (
              <p className="truncate font-texto text-caption text-texto-suave">
                {card.company_name}
              </p>
            )}
          </div>

          {destinos.length > 0 && (
            <MenuDoCard
              titulo={card.title}
              destinos={destinos}
              desabilitado={movendo}
              aoMover={aoMover}
              aoAbrir={aoAbrir}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          {/* A cifra veste a fonte de dados; a ausência dela, não. A mono
              alinha dígitos para a coluna ser lida de cima a baixo — e um funil
              recém-preenchido gritava "Sem valor" com a força reservada ao
              número que a pessoa veio comparar. */}
          {temValor(card.value_cents) ? (
            <p className="font-dados text-body tabular-nums text-texto">
              {formatarDinheiro(card.value_cents, card.currency)}
            </p>
          ) : (
            <p className="font-texto text-caption text-texto-tenue">{SEM_VALOR}</p>
          )}
          {etiqueta !== null && (
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 font-texto text-micro font-semibold ${etiqueta.classe}`}
            >
              {etiqueta.texto}
            </span>
          )}
        </div>

        {/* O contato de hoje vira tarja, e não mais uma etiqueta cinza: é a
            única informação do card que pede ação **hoje**, e ela some no meio
            de um texto do mesmo peso. */}
        {card.has_contact_today && card.next_contact_at !== null && (
          <p className="rounded-md bg-acao/10 px-2 py-1 font-texto text-caption font-semibold text-acao">
            <span aria-hidden="true">◷ </span>
            Contato hoje às {horaDoContato(card.next_contact_at)}
            {card.next_step !== null && ` — ${card.next_step}`}
          </p>
        )}

        {!card.has_contact_today && card.next_step !== null && (
          <p className="font-texto text-caption text-texto-suave">
            <span aria-hidden="true">▸ </span>
            {card.next_step}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-borda pt-3">
          {/* Responsável, prazo e silêncio dividem esta linha, nesta ordem: de
              quem é, para quando, e há quanto tempo parou. */}
          <div className="flex min-w-0 items-center gap-1.5">
            {responsavel !== null ? (
              <>
                <span
                  aria-hidden="true"
                  className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-acao/10 font-texto text-[9px] font-bold text-acao"
                >
                  {iniciais(responsavel)}
                </span>
                {/* O primeiro nome, e não as iniciais sozinhas.

                    O desenho original resolvia o rodapé só com o avatar, para
                    poupar a largura de que o valor precisa — e fazia sentido
                    enquanto cada pessoa via apenas o próprio funil. Com o board
                    do time inteiro (ADR-0015), "SA" ao lado de um texto vira
                    sigla da empresa aos olhos de quem lê, e a informação de
                    quem toca a frente some sem que nada acuse.

                    Primeiro nome custa pouca largura e é como o time se chama.
                    O nome completo continua no `title` e no leitor de tela. */}
                <span
                  title={responsavel}
                  className="max-w-[9ch] truncate font-texto text-micro font-semibold text-texto-suave"
                >
                  <span className="sr-only">Responsável: </span>
                  {primeiroNome(responsavel)}
                </span>
              </>
            ) : (
              // Card sem dono é acionável, e num board compartilhado ele
              // precisa se declarar: alguém tem de assumir. Silenciar aqui
              // deixaria a frente órfã indistinguível das demais.
              <span className="shrink-0 font-texto text-micro text-texto-tenue">
                Sem responsável
              </span>
            )}

            <span aria-hidden="true" className="shrink-0 font-texto text-micro text-texto-tenue">
              ·
            </span>

            {/* A fonte de dados alinha `15/09` sob `15/09`. Quando não há
                prazo, o que entra aqui é texto — e texto em mono, com ponto
                final, repetido em cada card novo, não alinha nada. */}
            {card.expected_close_date === null ? (
              <span className="truncate font-texto text-micro text-texto-tenue">
                {resumirSilencio(card.days_in_stage)}
              </span>
            ) : (
              <span className="truncate font-dados text-micro tabular-nums text-texto-tenue">
                {formatarData(card.expected_close_date)}
              </span>
            )}
          </div>

          <p className="flex shrink-0 items-center gap-2 font-dados text-micro tabular-nums text-texto-tenue">
            {card.overdue_task_count > 0 && (
              <span className="text-carmim">{card.overdue_task_count} atrasada(s)</span>
            )}
            {card.open_task_count > 0 && <span>{card.open_task_count} tarefa(s)</span>}
            {card.document_count > 0 && <span>{card.document_count} doc(s)</span>}
          </p>
        </div>

        {card.tags.length > 0 && (
          <ul className="flex flex-wrap gap-1">
            {card.tags.map((tag) => (
              <li
                key={tag}
                className="rounded-sm bg-canvas px-2 py-[2px] font-texto text-micro uppercase text-texto-tenue"
              >
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

interface Etiqueta {
  texto: string;
  classe: string;
}

/** O que a etiqueta diz depende de o negócio estar ou não em jogo.
 *
 * Fechado mostra o desfecho; aberto mostra a temperatura, que o servidor
 * derivou do tempo parado. Uma etiqueta "Quente" numa coluna de Ganho pediria
 * uma ação que não existe mais.
 */
function etiquetaDo(card: Card): Etiqueta | null {
  if (card.status === "won") return { texto: "Ganho", classe: "bg-musgo/10 text-musgo" };
  if (card.status === "lost") return { texto: "Perdido", classe: "bg-canvas text-texto-tenue" };

  switch (card.temperature) {
    case "hot":
      return { texto: "Quente", classe: "bg-musgo/10 text-musgo" };
    case "warm":
      return { texto: "Morno", classe: "bg-canvas text-texto-suave" };
    case "cold":
      // A única etiqueta que acende. Card parado é o que exige ação, e é o
      // que o âmbar existe para apontar.
      return { texto: "Frio", classe: "bg-ambar/15 text-ambar" };
    default:
      return null;
  }
}

/** Menu do card.
 *
 * `<details>` continua sendo a base: ele abre e fecha pelo teclado sem uma
 * linha de JavaScript, e o `summary` já se anuncia como controle.
 *
 * **O que ele não faz é fechar sozinho.** O comentário antigo aqui afirmava
 * que sim — "abre e fecha por teclado sozinho, um menu à mão precisaria
 * replicar foco, `Escape` e clique fora" —, e as duas últimas simplesmente não
 * existem no elemento nativo. Na tela, o menu ficava aberto até alguém clicar
 * de novo nos três pontos; abrir o de outro card deixava dois abertos ao mesmo
 * tempo. As trinta linhas abaixo são exatamente o que faltava, e nada além.
 */
/** A linha do menu, num lugar só: duas classes copiadas divergem na primeira
 * mudança de uma delas. */
const ITEM_DO_MENU =
  "w-full rounded-sm px-2 py-1.5 text-left font-texto text-caption text-texto " +
  "hover:bg-canvas focus-visible:outline focus-visible:outline-2 " +
  "focus-visible:-outline-offset-2 focus-visible:outline-acao-viva";

function MenuDoCard({
  titulo,
  destinos,
  desabilitado,
  aoMover,
  aoAbrir,
}: {
  titulo: string;
  destinos: readonly { id: string; nome: string }[];
  desabilitado: boolean;
  aoMover: (paraEtapa: string) => void;
  /** Abre a ficha. O mesmo destino do título.
   *
   * Com `marcarCompromisso`, ela abre já com o formulário: quem escolheu essa
   * linha do menu já disse o que quer fazer, e pedir mais um clique cobraria a
   * mesma decisão duas vezes. */
  aoAbrir: (marcarCompromisso?: boolean) => void;
}) {
  const menu = useRef<HTMLDetailsElement>(null);

  function fechar() {
    if (menu.current !== null) menu.current.open = false;
  }

  useEffect(() => {
    function aoApontarFora(evento: PointerEvent) {
      const alvo = evento.target;
      if (menu.current === null || !(alvo instanceof Node)) return;
      // `contains` e não comparação direta: o clique legítimo pode cair no
      // `select`, no rótulo ou no próprio `summary`, e todos são descendentes.
      if (!menu.current.contains(alvo)) menu.current.open = false;
    }

    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key !== "Escape" || menu.current === null) return;
      menu.current.open = false;
    }

    // `pointerdown`, e não `click`: fechar só no `click` deixaria o menu aberto
    // durante todo o arrasto de um card, que começa com o ponteiro pressionado
    // e pode nem terminar em clique.
    document.addEventListener("pointerdown", aoApontarFora);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("pointerdown", aoApontarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, []);

  return (
    <details ref={menu} className="relative shrink-0">
      <summary
        aria-label={`Ações de ${titulo}`}
        // 24×24 px violava o mínimo de 44 que o DESIGN.md § 8 pede no toque.
        // `sm:h-8 sm:w-8` no desktop, onde o ponteiro é preciso e um alvo de
        // 44 px roubaria espaço do título; `h-11 w-11` no celular, que é onde
        // a regra existe.
        className="grid h-11 w-11 cursor-pointer list-none place-items-center rounded-sm text-texto-tenue hover:bg-canvas hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva sm:h-8 sm:w-8 [&::-webkit-details-marker]:hidden"
      >
        <span aria-hidden="true">⋯</span>
      </summary>
      <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-borda bg-superficie p-2 shadow-e3">
        {/* Abrir vem primeiro, e mover depois: o arrasto já move, e o que o
            card não oferece de jeito nenhum é o caminho para dar baixa no
            contato ou marcar o próximo — os dois moram na ficha.

            No menu, e não na face do card: o card já carrega régua, título,
            empresa, valor, etiqueta, próximo passo, responsável, prazo e
            contadores. Botão por card, vezes quatro colunas, apaga a régua de
            silêncio — que é o elemento-assinatura do produto. */}
        <button
          type="button"
          onClick={() => {
            fechar();
            aoAbrir();
          }}
          className={ITEM_DO_MENU}
        >
          Abrir oportunidade
        </button>
        {/* O atalho do dia: depois de falar com o cliente, o que se faz é
            combinar o próximo encontro. Sem ele, o caminho era abrir a ficha,
            rolar até Compromissos e clicar de novo. */}
        <button
          type="button"
          onClick={() => {
            fechar();
            aoAbrir(true);
          }}
          className={ITEM_DO_MENU}
        >
          Marcar compromisso
        </button>

        <span aria-hidden="true" className="my-1 block h-px bg-borda" />

        <label className="flex flex-col gap-1">
          <span className="font-texto text-micro uppercase text-texto-tenue">Mover para</span>
          <select
            // Valor vazio e não a etapa atual: o campo é uma ação, não o
            // estado do card. Mostrá-lo preenchido sugeriria um seletor de
            // etapa que já está aplicado.
            value=""
            disabled={desabilitado}
            aria-label={`Mover ${titulo} para outra etapa`}
            onChange={(evento) => {
              if (evento.target.value !== "") aoMover(evento.target.value);
            }}
            className="h-8 rounded-sm border border-borda bg-canvas px-2 font-texto text-caption text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
          >
            <option value="">Escolher etapa…</option>
            {destinos.map((etapa) => (
              <option key={etapa.id} value={etapa.id}>
                {etapa.nome}
              </option>
            ))}
          </select>
        </label>
      </div>
    </details>
  );
}
